import React, { useState, useMemo } from 'react';
import { defineMessages, FormattedMessage } from '@kyruus/intl';
import { withRouter } from 'react-router-dom';

import querystring from 'querystring';
import ProviderTile from '@kyruus/provider-tile';
import ProviderTileNext, {
  PLACEMENT_TYPE_SEARCH
} from '@kyruus/provider-tile-next';
import { availabilityTilesTracking } from '@kyruus/availability-tiles';
import {
  ProviderReferralButton,
  ProviderReferralList
} from '@kyruus/provider-referral';

import CtaModal from '../cta-modal';

import { getProviderDisplayName_V8 } from '../provider/utils';
import { ContactPanel } from '../provider/scheduling-options';
import { pages, BOOK_TRACKING_PARAM_NAME } from '../tracking/constants';
import { pageViewEvent } from '../tracking/tracking-utils';
import { getLogWithV8Provider } from '../tracking/decorators';
import { getRelativeParameterizedBookingUrl } from '../utils/getRelativeParameterizedBookingUrl';
import { locationsModuleEnabled, buildLocationURL } from '../utils/location';

import { MAX_AVAILABILITIES_TO_DISPLAY } from '../utils/constants';
import { scrollToTopCCF } from '../utils/cerner';
import { useReferralCrmConnector } from '../referral-crm-connector';
import { withQueryParam } from '../utils/url';
import {
  MODULES,
  isModuleEnabled,
  shouldRenderDirectBookInDrawer
} from 'Common/config';
import { getProviderProfilePathV8 } from 'Common/urls/provider';
import { messages as crmMessages } from 'Src/referral-crm-connector/useCrmConnector/messages';

const messages = defineMessages({
  header: {
    id: 'cta.modal.header',
    description: 'Book an appointment call to action modal header',
    defaultMessage: 'Book an Appointment with {providerName}'
  },
  moreAvailabilitiesLinkText: {
    id: 'availability.moreavailabilities',
    description: 'Text to display on link to view more availabilities',
    defaultMessage: 'View All Appointments'
  },
  availabilityErrorMsg: {
    id: 'availability.error',
    description: 'Message to display when call to availability fails',
    defaultMessage:
      "We're currently experiencing difficulties fetching availability for {displayName}."
  },
  availabilityNoMoreRetriesMsg: {
    id: 'availability.nomoreretries',
    description:
      'Message to display when user can no longer retry call to get slots',
    defaultMessage:
      "Unfortunately we can't fetch availability for {displayName} right now. Please call {phoneNumber} to schedule an appointment."
  },
  availabilityTryAgainBtnText: {
    id: 'availability.tryagain',
    description: 'Text for try availability "try again" button',
    defaultMessage: 'Try again.'
  },
  showAvailabilitiesBtnText: {
    id: 'availability.showavailabilities',
    description: 'Text for availability CTA button',
    defaultMessage: 'Show Available Appointments'
  },
  noAvailabilityMessage: {
    id: 'availability.noavailability',
    description: 'Message to display when a location has no available slots',
    defaultMessage:
      "{displayName} doesn't have any availability for this location."
  }
});

// constants for the values of the `from` url param when passing from providers list to profile pages
export const FROM_SEARCH_LIST = 'search-list';
export const FROM_SEARCH_MAP = 'search-map';
export const FROM_LOCATIONS = 'locations';

export const getSummaryUrl = (
  provider,
  hash,
  getProviderUrl,
  slots,
  profileSummaryParams = {}
) => {
  const base = getProviderUrl(provider, hash);
  const [path, search] = base.split('?');
  const [query, _hash] = (search || '').split('#');
  const baseQuery = querystring.parse(query);

  const updatedQuery = {
    ...baseQuery,
    ...profileSummaryParams
  };

  if (slots && slots[provider.id]) {
    const {
      apptInfo: { relationship, purpose }
    } = slots[provider.id];

    const updatedApptInfo = {
      ...updatedQuery,
      ...{
        relationship,
        purpose
      }
    };

    return `${path}?${querystring.stringify(updatedApptInfo)}${
      _hash ? `#${_hash}` : ''
    }`;
  }

  return `${path}?${querystring.stringify(updatedQuery)}${
    _hash ? `#${_hash}` : ''
  }`;
};

function ProviderList({
  // array of providers
  providers = [],
  customerCode,
  productName,
  // customer config
  config,
  // ?
  searchSummary,
  // callback for logging to kloggyr
  log,
  // url matched params from react-router
  match,
  // window location from react-router
  location,
  // history object for navigation from react-router
  history,
  // ?
  onClickShowAvailability,
  // ?
  availabilityLinkTarget,
  // callback for "onTimesSlots"
  onTimeSlotsClick,
  // callback when clicking view more availability tiles button
  onViewMoreClick,
  // callback when availability tiles are loaded
  onAvailabilityTilesLoaded,
  // ?
  slots,
  // ?
  slotsStatus,
  // extra params to send to send along to provider profile pages
  profileSummaryParams = {},
  // provider title and ratings callback function
  providerCallbackFunc = () => {},
  // booking appt button callback function
  bookingCallbackFunc = () => {},
  // appointment options to be passed as deepLinkingParam
  apptOptions
}) {
  // State for populating and triggering modal
  const [selectedProvider, setSelectedProvider] = useState(undefined);

  const shouldDisplayAvailability = config.display_availability_in_search;
  const isLocationsModuleEnabled = locationsModuleEnabled(config);

  const { enabled: crmEnabled, connector } = useReferralCrmConnector(config);

  // Get modal display
  const {
    modal_display: { skip_modal: skipModal, virtual_care: virtualCareOrg } = {}
  } = config;

  const logWithSelectedProvider = useMemo(
    () => getLogWithV8Provider(log, selectedProvider),
    [log, selectedProvider]
  );

  const bookingCallbackFunction = (provider) => {
    return (_e) => {
      bookingCallbackFunc(provider);
      const providerBookUrl = provider.book_online_url;
      const providerVirtualCareUrl = provider.virtual_care_url;
      const virtualCareEnabled = providerVirtualCareUrl && virtualCareOrg;
      // the boolean skipModal comes from an org's config and indicates that the call to action button should bypass the modal
      // however, this feature is only active if the org does not have both direct booking and virtual care configured
      // in the case that skipModal is turned on and direct booking and virtual care are active,
      // we render the modal so the user can select which option they prefer.
      // If we are rendering direct book in a drawer, always skip the modal.
      const canSkipModal =
        shouldRenderDirectBookInDrawer(config) ||
        (skipModal &&
          (providerBookUrl || virtualCareEnabled) &&
          !(providerBookUrl && virtualCareEnabled));
      if (canSkipModal) {
        const url =
          getRelativeParameterizedBookingUrl({
            provider,
            config,
            location
          }) || provider.virtual_care_url;
        if (shouldRenderDirectBookInDrawer(config)) {
          // Push the current URL onto the history stack
          // along with the selected provider data as part of the location state
          // as required for react-router v4 modal routing.
          // See https://v5.reactrouter.com/web/example/modal-gallery for more info.
          history.push(url, {
            provider: {
              location,
              provider
            }
          });
        } else if (shouldOpenDirectBookSameWindow()) {
          window.open(url, '_self');
        } else {
          const win = window.open(url, '_blank');
          if (win != null) {
            win.focus();
          }
        }
      } else {
        scrollToTopCCF();
        // treat showing the modal as a "page event"
        getLogWithV8Provider(log, provider)(pageViewEvent(pages.CTA_MODAL));
        setSelectedProvider(provider);
      }
    };
  };

  const getProviderUrl = (provider, hash = '') => {
    //const baseUrl = window.location.origin;
    const pathname = getProviderProfilePathV8(provider);
    let search = location.search;
    if (!search && match.params.specialty) {
      const nearParam = match.params.location
        ? `&location=${match.params.location}`
        : '';
      search = `?specialty_page=true&specialty=${match.params.specialty}${nearParam}`;
    }
    const url = `${pathname}${search}${hash}`;
    return url;
  };

  const providerCallbackFunction = (provider, hash = '', slots) => {
    return (e) => {
      providerCallbackFunc(provider);
      const url = getSummaryUrl(
        provider,
        hash,
        getProviderUrl,
        slots,
        profileSummaryParams
      );
      if (e.ctrlKey || e.metaKey) {
        window.open(url);
      } else {
        // force a server side render so that the redux context is reset with the search v9 provider (vs the v8 provider)
        window.open(url, '_self');
        // TODO: once search v9 migration is complete on the searchmatch page, switch to the more performant react-router history:
        // history.push(url);
      }
    };
  };

  const hideBookingModal = (event) => {
    // The close event triggered by the close button passes through an event, while the backdrop does not
    const closeTrigger = event ? 'close_icon' : 'backdrop';
    // log(`user_action.alter_view.request_appointment.${closeTrigger}`, {
    //   provider_id: selectedProvider.id
    // });
    getLogWithV8Provider(
      log,
      selectedProvider
    )(`user_action.alter_view.request_appointment.${closeTrigger}`);
    setSelectedProvider(undefined);
  };

  const shouldOpenDirectBookSameWindow = () => {
    return isModuleEnabled(config, MODULES.DIRECT_BOOK);
  };

  const getProviderContactPhone = (provider) => {
    const phoneContact = provider.contacts.find((contact) => {
      return contact.contact_type === 'phone';
    });
    if (phoneContact) {
      return phoneContact.value;
    }
  };

  const locationCallbackFunction = (locationUrl) => {
    log('user_action.display_location_profile_page');
    history.push(locationUrl);
  };

  let bookingModal = null;

  if (selectedProvider) {
    // Variables for panel creation
    const providerId = selectedProvider.id;
    const providerBookUrl = selectedProvider.book_online_url;
    const providerPhone = getProviderContactPhone(selectedProvider);
    const providerRequestUrl = selectedProvider.request_appointment_url;
    const providerVirtualCareUrl = selectedProvider.virtual_care_url;
    const virtualCareEnabled = providerVirtualCareUrl && virtualCareOrg;

    const bookUrlTarget = shouldOpenDirectBookSameWindow()
      ? '_self'
      : undefined;

    let bookOnline, phone, requestForm, virtualCare;

    if (providerBookUrl) {
      bookOnline = (
        <ContactPanel
          target={bookUrlTarget}
          contact={getRelativeParameterizedBookingUrl({
            provider: selectedProvider,
            config,
            location,
            apptOptions
          })}
          contactType={'book_online'}
          log={logWithSelectedProvider}
          providerId={providerId}
        />
      );
    }
    if (providerPhone) {
      phone = (
        <ContactPanel
          contact={providerPhone}
          contactType={'phone'}
          log={logWithSelectedProvider}
          providerId={providerId}
        />
      );
    }
    if (providerRequestUrl) {
      requestForm = (
        <ContactPanel
          contact={providerRequestUrl}
          contactType={'request_form'}
          log={logWithSelectedProvider}
          providerId={providerId}
        />
      );
    }
    if (virtualCareEnabled) {
      virtualCare = (
        <ContactPanel
          contact={providerVirtualCareUrl}
          contactType={'virtual_care'}
          log={logWithSelectedProvider}
          providerId={providerId}
        />
      );
    }

    const panelCount = [bookOnline, requestForm, phone, virtualCare].filter(
      Boolean
    ).length;

    bookingModal = (
      <CtaModal
        id="appt-request"
        title={
          <FormattedMessage
            {...messages.header}
            values={{
              providerName: getProviderDisplayName_V8(selectedProvider)
            }}
          />
        }
        panels={[bookOnline, phone, requestForm, virtualCare]}
        open={Boolean(selectedProvider)}
        onClose={hideBookingModal}
        panelCount={panelCount}
      />
    );
  }

  const getProviderSlotsAndApptInfo = (provider, slots) => {
    return slots && slots[provider.id] ? slots[provider.id] : {};
  };

  const getProviderSlotsStatus = (provider, slotsStatus) => {
    return slotsStatus && slotsStatus[provider.id]
      ? slotsStatus[provider.id]
      : null;
  };
  const providerCards = providers.map((provider, index) => {
    const bookOnlineUrl = getRelativeParameterizedBookingUrl({
      provider,
      config,
      location
    });
    if (crmEnabled) {
      return (
        <ProviderTileNext
          provider={provider}
          customerCode={customerCode}
          productName={productName}
          providerLocations={provider.locations}
          bookOnlineUrl={bookOnlineUrl}
          config={config}
          searchSummary={searchSummary}
          track={getLogWithV8Provider(log, provider)}
          bookingCallback={bookingCallbackFunction(provider)}
          reviewCallback={providerCallbackFunction(
            provider,
            '#provider-details-reviews',
            slots
          )}
          reviewUrl={getProviderUrl(provider, '#provider-details-reviews')}
          summaryCallback={providerCallbackFunction(provider, '', slots)}
          summaryUrl={getSummaryUrl(
            provider,
            '',
            getProviderUrl,
            slots,
            profileSummaryParams
          )}
          key={provider.id}
          lazyLoadImg={index > 2}
          onClickShowAvailability={() => onClickShowAvailability(provider)}
          availabilityTilesMessages={{
            moreAvailabilitiesLinkText: (
              <FormattedMessage {...messages.moreAvailabilitiesLinkText} />
            ),
            showAvailabilitiesBtnText: (
              <FormattedMessage {...messages.showAvailabilitiesBtnText} />
            ),
            noAvailabilityMessage: (
              <FormattedMessage
                {...messages.noAvailabilityMessage}
                values={{ displayName: getProviderDisplayName_V8(provider) }}
              />
            ),
            availabilityErrorMsg: (
              <FormattedMessage
                {...messages.availabilityErrorMsg}
                values={{ displayName: getProviderDisplayName_V8(provider) }}
              />
            ),
            availabilityNoMoreRetriesMsg: (
              <FormattedMessage
                {...messages.availabilityNoMoreRetriesMsg}
                values={{
                  displayName: getProviderDisplayName_V8(provider),
                  phoneNumber: (() => {
                    const phone = provider.contacts.find(({ contact_type }) => {
                      return contact_type === 'phone';
                    });
                    if (phone) {
                      return phone.value;
                    }
                  })()
                }}
              />
            ),
            availabilityTryAgainBtnText: (
              <FormattedMessage {...messages.availabilityTryAgainBtnText} />
            )
          }}
          availabilityLinkTarget={availabilityLinkTarget}
          maxAvailabilitiesToDisplay={MAX_AVAILABILITIES_TO_DISPLAY}
          onTimeSlotsClick={onTimeSlotsClick}
          onViewMoreClick={onViewMoreClick}
          onAvailabilityTilesLoaded={onAvailabilityTilesLoaded}
          getLocationUrl={(location) =>
            buildLocationURL(
              isLocationsModuleEnabled,
              location,
              searchSummary.query_string
            )
          }
          locationCallback={(locationUrl) =>
            locationCallbackFunction(locationUrl)
          }
          apptInfo={getProviderSlotsAndApptInfo(provider, slots).apptInfo}
          slots={getProviderSlotsAndApptInfo(provider, slots).slots}
          slotsStatus={getProviderSlotsStatus(provider, slotsStatus)}
          locationCTA={
            isModuleEnabled(config, MODULES.REFERRAL) &&
            ((props) => (
              <ProviderReferralButton
                referrals={connector.state.context.referrals}
                onAddReferral={connector.addReferral}
                onRemoveReferral={connector.removeReferral}
                canRefer={connector.canRefer(connector.state)}
                hasMaxReferrals={connector.hasMaxReferrals(connector.state)}
                referableProvider={{ ...props, config: config }}
              />
            ))
          }
          placementType={PLACEMENT_TYPE_SEARCH}
          bookingWarning={
            crmEnabled && (
              <FormattedMessage {...crmMessages.invalidPatientDataBooking} />
            )
          }
          hasBookingWarning={
            isModuleEnabled(config, MODULES.DIRECT_BOOK) &&
            crmEnabled &&
            connector.hasPatientValidationErrorsForBooking()
          }
          isBookingDisabled={crmEnabled && !connector.canBook(connector.state)}
          isProfileDisabled={!isModuleEnabled(config, MODULES.PROFILE)}
        />
      );
    }
    return (
      <ProviderTile
        provider={provider}
        // V8 Provider tile does not automatically append tracking for availability tiles to its booking urls.
        // However, bookOnlineUrl happens to only be used for the availability_tiles links in this placement, as the regular links
        // go through the bookingCallback for navigation, so we append the tracking param here
        bookOnlineUrl={withQueryParam(
          bookOnlineUrl,
          BOOK_TRACKING_PARAM_NAME,
          availabilityTilesTracking.TRACKING_SOURCE_SEARCH
        )}
        config={config}
        searchSummary={searchSummary}
        track={getLogWithV8Provider(log, provider)}
        bookingCallback={bookingCallbackFunction(provider)}
        reviewCallback={providerCallbackFunction(
          provider,
          '#provider-details-reviews',
          slots
        )}
        reviewUrl={getProviderUrl(provider, '#provider-details-reviews')}
        summaryCallback={providerCallbackFunction(provider, '', slots)}
        summaryUrl={getSummaryUrl(
          provider,
          '',
          getProviderUrl,
          slots,
          profileSummaryParams
        )}
        key={provider.id}
        lazyLoadImg={index > 2}
        onClickShowAvailability={() => onClickShowAvailability(provider)}
        availabilityTilesMessages={{
          moreAvailabilitiesLinkText: (
            <FormattedMessage {...messages.moreAvailabilitiesLinkText} />
          ),
          showAvailabilitiesBtnText: (
            <FormattedMessage {...messages.showAvailabilitiesBtnText} />
          ),
          noAvailabilityMessage: (
            <FormattedMessage
              {...messages.noAvailabilityMessage}
              values={{ displayName: getProviderDisplayName_V8(provider) }}
            />
          ),
          availabilityErrorMsg: (
            <FormattedMessage
              {...messages.availabilityErrorMsg}
              values={{ displayName: getProviderDisplayName_V8(provider) }}
            />
          ),
          availabilityNoMoreRetriesMsg: (
            <FormattedMessage
              {...messages.availabilityNoMoreRetriesMsg}
              values={{
                displayName: getProviderDisplayName_V8(provider),
                phoneNumber: (() => {
                  const phone = provider.contacts.find(({ contact_type }) => {
                    return contact_type === 'phone';
                  });
                  if (phone) {
                    return phone.value;
                  }
                })()
              }}
            />
          ),
          availabilityTryAgainBtnText: (
            <FormattedMessage {...messages.availabilityTryAgainBtnText} />
          )
        }}
        availabilityLinkTarget={availabilityLinkTarget}
        shouldDisplayAvailability={shouldDisplayAvailability}
        maxAvailabilitiesToDisplay={MAX_AVAILABILITIES_TO_DISPLAY}
        onTimeSlotsClick={onTimeSlotsClick}
        onAvailabilityTilesLoaded={onAvailabilityTilesLoaded}
        getLocationUrl={(location) =>
          buildLocationURL(
            isLocationsModuleEnabled,
            location,
            searchSummary.query_string
          )
        }
        locationCallback={(locationUrl) =>
          locationCallbackFunction(locationUrl)
        }
        apptInfo={getProviderSlotsAndApptInfo(provider, slots).apptInfo}
        slots={getProviderSlotsAndApptInfo(provider, slots).slots}
        slotsStatus={getProviderSlotsStatus(provider, slotsStatus)}
      />
    );
  });

  return (
    <div data-testid="ProviderList" className="result-column">
      {providerCards}
      {isModuleEnabled(config, MODULES.REFERRAL) && (
        <ProviderReferralList
          referrals={connector.state.context.referrals}
          maxReferralCount={connector.state.context.maxReferralCount}
          canRefer={connector.canRefer(connector.state)}
          hasReferrals={connector.hasReferrals(connector.state)}
          isReferDisabled={connector.isReferDisabled(connector.state)}
          showSpinner={connector.showSpinner(connector.state)}
          onRemoveReferral={connector.removeReferral}
          onSendReferralData={(referralMetaData) => {
            log('user_action.provider_detail.refer_provider', {
              referrals: connector.state.context.referrals.map((ref) => ({
                provider_id: ref.provider.id,
                location_id: ref.location.id
              }))
            });
            connector.sendReferralData(referralMetaData);
          }}
          referralMetaData={{ searchSummary }}
        />
      )}
      {bookingModal}
    </div>
  );
}

export default withRouter(ProviderList);
